import { useEffect, useState } from "react";
import { Coins, Trophy, Ticket, Link2, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <div data-testid={testId} className="bg-black/30 border border-zinc-800 rounded-lg p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500"><Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}</div>
    <div className="font-head text-2xl font-semibold mt-1">{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-0.5 truncate">{sub}</div>}
  </div>
);

export const MyStatsCard = ({ guild }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const did = user?.discord_id;
  const gid = guild?.discord_id;

  useEffect(() => {
    if (!did || !gid) return;
    setData(null); setError("");
    const q = `?guild_id=${gid}`;
    Promise.all([api.get(`/user_stats/economy/${did}${q}`), api.get(`/user_stats/leveling/${did}${q}`), api.get(`/user_stats/tickets/status/${did}${q}`)])
      .then(([e, l, t]) => setData({ eco: e.data, lvl: l.data, tickets: t.data.active_tickets }))
      .catch((e) => setError(errMsg(e)));
  }, [did, gid]);

  let body;
  if (!did) body = <p data-testid="my-stats-link-discord" className="text-sm text-zinc-400 flex items-center gap-2"><Link2 className="w-4 h-4 text-volt" /> Log in with Discord once to link your account and see your balance, level and tickets here.</p>;
  else if (!gid) body = <p data-testid="my-stats-no-server" className="text-sm text-zinc-500">This server isn't linked to Discord yet — invite the bot to see your stats.</p>;
  else if (error) body = <p data-testid="my-stats-error" className="text-sm text-red-400">{error}</p>;
  else if (!data) body = <p className="text-sm text-zinc-500">Loading your stats…</p>;
  else body = (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Stat testId="my-stats-balance" icon={Coins} accent="#facc15" label="Balance"
        value={data.eco.enabled ? data.eco.balance.toLocaleString() : "—"}
        sub={data.eco.enabled ? `${data.eco.wallet.toLocaleString()} wallet · ${data.eco.bank.toLocaleString()} bank` : "Economy is off in this server"} />
      <Stat testId="my-stats-level" icon={Trophy} accent="#0EA5E9" label="Level" value={data.lvl.level}
        sub={`${data.lvl.xp.toLocaleString()} XP${data.lvl.rank ? ` · rank #${data.lvl.rank}` : ""}`} />
      <Stat testId="my-stats-tickets" icon={Ticket} accent="#f59e0b" label="Open tickets" value={data.tickets.length}
        sub={data.tickets.length ? "Listed below" : "None open"} />
      {data.tickets.length > 0 && (
        <ul data-testid="my-stats-ticket-list" className="sm:col-span-3 divide-y divide-zinc-800 border border-zinc-800 rounded-lg bg-black/20">
          {data.tickets.map((t) => (
            <li key={t.id} data-testid={`my-stats-ticket-${t.number}`} className="px-4 py-2 text-sm flex items-center gap-2 min-w-0">
              <span className="font-mono text-[#f59e0b] shrink-0">#{String(t.number).padStart(4, "0")}</span>
              <span className="text-zinc-500">—</span>
              <span className="truncate">{t.subject || "No subject"}</span>
              {t.claimed_by && <span className="ml-auto text-xs text-zinc-500 shrink-0">claimed by {t.claimed_by}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div data-testid="my-stats-card" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-head font-semibold">My Stats</h3>
        <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" /> only visible to you</span>
      </div>
      {body}
    </div>
  );
};
